<template>
  <div
    :class="{
      'drag-and-drop-area': true,
      'drag-and-drop-area--bg-color-white': props.bgColor === 'white',
      'drag-and-drop-area--disabled': props.isDisabled === true,
    }"
  >
    <UploadPhotosNonAdventure
      class="drag-and-drop-area__upload-form"
      :customerId="props.customerId"
      @uploadingStarted="
        () => {
          isUploadingStarted = true;
          _isUploadInProgress = true;
        }
      "
      @imageUploadedToServer="({ serverId }) => emit('update:imagesList', [...props.imagesList, serverId])"
      @uploadProgressChange="isUploadInProgress => (_isUploadInProgress = isUploadInProgress)"
      style="opacity: 0 !important"
      :isFilesLimitReached="props.isFilesLimitReached"
      :flow="props.flow"
    />

    <!-- "How many files to upload" note -->
    <div class="how-many-files-to-upload-note drag-and-drop-area__how-many-files-to-upload-note">
      <IconEmbedded class="how-many-files-to-upload-note__icon" name="upload_1-5" :size="60" color="rgba(36, 61, 74, 0.5)" />

      <template v-if="!slots.howManyFilesToUploadNote">
        <h3 v-if="!props.imagesList.length" class="global-h3 how-many-files-to-upload-note__title">
          Upload {{ String(props.imagesLimitRange[0]) + (props.imagesLimitRange[1] ? `-${props.imagesLimitRange[1]}` : "+") }} {{ humanReadableAcceptedFileTypesNote }} to get started<br />
          <template v-if="!props.imagesLimitRange[1]">(the more the better)</template>
        </h3>

        <h3 v-else class="global-h3 how-many-files-to-upload-note__title">
          <template v-if="props.isFilesLimitReached">
            <b>{{ props.imagesList.length }}</b> added!
          </template>
          <template v-else>
            <b>{{ props.imagesList.length }}</b> added, click to upload more!
          </template>
        </h3>
      </template>

      <template v-else>
        <slot
          name="howManyFilesToUploadNote"
          :uploadedFilesNumber="props.imagesList.length"
          :imagesLimitRange="props.imagesLimitRange"
          :humanReadableAcceptedFileTypesNote="humanReadableAcceptedFileTypesNote"
        />
      </template>
    </div>
    <!-- / "How many files to upload" note -->

    <slot name="guidanceSchema" :isUploadInProgress="_isUploadInProgress" :imagesListLength="props.imagesList.length" />

    <template v-if="props.imagesList.length || _isUploadInProgress">
      <div class="drag-and-drop-area__divider"></div>

      <!-- Thumbnails row -->
      <div class="thumbnails-row drag-and-drop-area__thumbnails-row">
        <ul class="thumbnails-row__row-wrap">
          <SrpFileThumbnail class="thumbnails-row__thumbnail" v-for="fileName in props.imagesList.slice(-10)" :key="fileName" :fileName="fileName" :isFixedAspectRatio="false" />

          <li v-if="_isUploadInProgress" class="thumbnails-row__thumbnail-placeholder">
            <img src="/images/thumbnails_loader7.gif" alt="loader" style="width: 40px" />
          </li>
        </ul>
      </div>
      <!-- / Thumbnails row -->

      <!-- Upload progressbar -->
      <div v-if="props.imagesList.length < props.imagesLimitRange[0]" class="upload-progressbar drag-and-drop-areaq__upload-progressbar">
        <div class="upload-progressbar__title">
          <b>{{ props.imagesLimitRange[0] - props.imagesList.length }}</b> more needed
        </div>
        <div class="upload-progressbar__bar-track">
          <div class="upload-progressbar__bar" :style="{ width: `${props.imagesList.length / (props.imagesLimitRange[0] / 100)}%` }"></div>
        </div>
      </div>
      <!-- / Upload progressbar -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, useSlots, computed } from "vue";
import { UploadFlow } from "@/logic/ImageUploader";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import UploadPhotosNonAdventure from "@views/CMS/UploadPhotosNonAdventure.vue";

// Slots
const slots = useSlots();

const props = withDefaults(
  defineProps<{
    customerId?: string;
    imagesLimitRange?: [number, number]; // e.g. [10,0], [0,10], [10,20] ...
    imagesList: Array<string>;
    bgColor?: "pale-blue-gray" | "white";
    isDisabled?: boolean;
    isFilesLimitReached?: boolean;
    acceptedFileTypes?: Array<"photo" | "video">; // TODO: Implement a programmatic file-type blocker/filter
    flow?: UploadFlow;
  }>(),
  {
    customerId: "",
    imagesLimitRange: () => [20, 0],
    imagesList: () => [],
    bgColor: "pale-blue-gray",
    isDisabled: false,
    isFilesLimitReached: false,
    acceptedFileTypes: () => ["photo", "video"],
    flow: null,
  }
);

const emit = defineEmits<{
  (e: "update:imagesList", value: Array<string>): void;
}>();

// Is Uploading started =======================================================
const isUploadingStarted = ref<boolean>(false);

// Is upload in progress ======================================================
const _isUploadInProgress = ref<boolean>(false);

// Human-readable Accepted file types note ====================================
const humanReadableAcceptedFileTypesNote = computed<string>(() => {
  const arePhotosAccepted = props.acceptedFileTypes.includes("photo");
  const areVideosAccepted = props.acceptedFileTypes.includes("video");

  if (arePhotosAccepted && areVideosAccepted) {
    return "photos or videos";
  } else {
    if (arePhotosAccepted) return "photos";
    if (areVideosAccepted) return "videos";
  }

  return "";
});
</script>

<style scoped lang="scss">
// "How many files to upload" note ============================================
.how-many-files-to-upload-note {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__icon {
    margin-bottom: 6px;
  }

  &__title {
    font-weight: 500;
    text-align: center;
  }
}

// Thumbnails row =============================================================
.thumbnails-row {
  width: calc(100% - 20px);
  max-width: 500px;
  height: 50px;
  list-style: none;
  display: flex;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;

  -webkit-mask: linear-gradient(-90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);
  mask: linear-gradient(-90deg, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0) 100%);

  -webkit-mask-composite: destination-in;
  mask-composite: exclude;

  &__row-wrap {
    width: 2000px;
    min-width: 2000px;
    padding: 0;
    margin: 0;
    gap: 5px;
    display: flex;
    justify-content: flex-end;
  }

  &__thumbnail {
    height: 50px;
    display: inline-block;
  }

  &__thumbnail-placeholder {
    width: 60px;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.1);
  }
}

// Upload progressbar =========================================================
.upload-progressbar {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__title {
  }

  &__bar-track {
    width: 250px;
    max-width: calc(100% - 20px);
    height: 6px;
    border-radius: 100px;
    display: flex;
    justify-content: flex-start;
    background: rgba(203, 203, 203, 1);
  }

  &__bar {
    height: 6px;
    border-radius: 100px;
    background: rgba(5, 133, 135, 1);
  }
}

// Drag & Drop area ===========================================================
.drag-and-drop-area {
  min-height: 200px;
  padding: 35px 0 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;

  @mixin transparentForClicks {
    pointer-events: none;
    user-select: none;
  }

  &::before {
    @include transparentForClicks;
    content: "";
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border: 2px rgba(167, 172, 174, 1) dashed;
    border-radius: 6px;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    background: rgba(231, 237, 240, 1);
    transition:
      width 0.07s ease-in-out,
      height 0.07s ease-in-out,
      inset 0.07s ease-in-out;
  }
  &:hover::before {
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    inset: -3px auto auto -3px;
  }

  &--bg-color-white {
    &::before {
      background: rgba(255, 255, 255, 1);
    }
  }

  &__upload-form {
    width: 100%;
    height: calc(100% + 30px);
    min-height: calc(100% + 30px);
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    opacity: 0;

    & > * {
      width: 100%;
      height: 100%;
    }
  }

  &__divider {
    @include transparentForClicks;
    width: calc(100% - 30px);
    max-width: 520px;
    margin-bottom: 25px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  // before upload starts -------------
  &__how-many-files-to-upload-note {
    @include transparentForClicks;
    margin-bottom: 25px;
  }

  &__ai-post-gen-visual-schema {
    @include transparentForClicks;
  }

  // after upload starts --------------
  &__thumbnails-row {
    @include transparentForClicks;
    margin-bottom: 20px;
  }

  &__upload-progressbar {
    @include transparentForClicks;
  }
}
</style>
